import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const BannerCard = ({ banner, onEdit, onDelete }) => {
    // Parse highlights if it's a string
    const highlights = (() => {
        try {
            const parsed = typeof banner.highlights === 'string'
                ? JSON.parse(banner.highlights)
                : banner.highlights;
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Error parsing highlights for card:', e);
            return [];
        }
    })();

    return (
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border border-slate-100">
            {/* Banner Image */}
            <div className="relative aspect-video bg-gradient-to-br from-primary-100 to-primary-200">
                {banner.image_url ? (
                    <img
                       src={`${banner.image_url.startsWith('http') ? banner.image_url : (banner.image_url.startsWith('/') ? banner.image_url : `/${banner.image_url}`)}?t=${banner.updated_at ? new Date(banner.updated_at).getTime() : Date.now()}`}

                        alt={banner.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                        <span className="text-sm font-medium">No Image</span>
                    </div>
                )}

                {/* Display Order Badge */}
                <div className="absolute top-3 right-3 bg-white rounded-lg px-3 py-1.5 shadow-lg">
                    <span className="text-sm font-bold text-primary-600">
                        #{banner.display_order || 0}
                    </span>
                </div>

                {/* Active/Inactive Badge */}
                <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg ${banner.is_active
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-400 text-white'
                        }`}>
                        {banner.is_active ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="p-5">
                {/* Title */}
                <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-1">
                    {banner.title}
                </h3>

                {/* Subtitle */}
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                    {banner.subtitle}
                </p>

                {/* Highlights */}
                {highlights.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {highlights.slice(0, 3).map((highlight, index) => (
                            <span
                                key={index}
                                className="px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-md"
                            >
                                {highlight}
                            </span>
                        ))}
                        {highlights.length > 3 && (
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-md">
                                +{highlights.length - 3} more
                            </span>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t border-slate-100">
                    <button
                        onClick={() => onEdit(banner)}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 px-4 py-2.5 rounded-xl font-medium transition-all duration-200"
                    >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                    </button>
                    <button
                        onClick={() => onDelete(banner.id)}
                        className="flex-1 flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2.5 rounded-xl font-medium transition-all duration-200"
                    >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BannerCard;
